<template>
    <section class="rulebook-overview">
        <b-alert v-if="error" show variant="danger" class="d-flex align-items-center justify-content-between">
            <span>{{ error }}</span>
            <b-button size="sm" variant="outline-danger" @click="$emit('retry')">다시 불러오기</b-button>
        </b-alert>

        <div v-if="loading" class="text-center text-muted py-5">
            <b-spinner small class="mr-2" />판단 기준 상태를 불러오는 중입니다.
        </div>

        <template v-else>
            <b-card class="operations-card mb-4" body-class="p-4">
                <div class="d-flex flex-column flex-lg-row justify-content-between mb-3">
                    <div>
                        <div class="eyebrow mb-2">현재 운영 상태</div>
                        <h5 class="mb-0">{{ activationMode.label }}</h5>
                    </div>
                    <div class="mt-3 mt-lg-0">
                        <b-badge :variant="automationStatus.variant" class="status-badge">
                            {{ automationStatus.label }}
                        </b-badge>
                        <b-badge v-if="runningBadgeVisible" variant="primary" class="status-badge ml-2">
                            실행 중
                        </b-badge>
                    </div>
                </div>

                <b-alert v-if="!hasActiveVersion" show variant="warning" class="mb-3">
                    <div class="font-weight-bold mb-1">기본 기준 사용 중</div>
                    <div class="mb-2">
                        아직 활성 버전이 없어 파일에 들어 있는 기본 기준을 그대로 쓰고 있습니다.
                        이 기본 기준을 v1 으로 발행하면 이후 버전 이력과 복귀를 쓸 수 있습니다.
                    </div>
                    <b-button size="sm"
                              variant="primary"
                              :disabled="controlsDisabled || bootstrapPending"
                              @click="startBootstrap"
                    >
                        <b-spinner v-if="bootstrapPending" small class="mr-1" />기본 기준 발행
                    </b-button>
                </b-alert>

                <b-row>
                    <b-col cols="12" md="6" xl="3" class="mb-3 mb-xl-0">
                        <div class="stat-box">
                            <span class="field-label">활성 버전</span>
                            <div class="stat-value">{{ activeVersionLabel }}</div>
                            <small class="text-muted">원본 {{ activeVersionOrigin }}</small>
                        </div>
                    </b-col>
                    <b-col cols="12" md="6" xl="3" class="mb-3 mb-xl-0">
                        <div class="stat-box">
                            <span class="field-label">활성 시각</span>
                            <div class="stat-value small-value">{{ activatedAtLabel }}</div>
                            <small class="text-muted">원칙 {{ overview.rulebook.principleCount }}개</small>
                        </div>
                    </b-col>
                    <b-col cols="12" md="6" xl="3" class="mb-3 mb-xl-0">
                        <div class="stat-box">
                            <span class="field-label">오늘 실행 회차</span>
                            <div class="stat-value">{{ todayCycles }} / {{ maxCycles }}</div>
                            <small class="text-muted">회차당 최대 {{ maxSources }}건 반영</small>
                        </div>
                    </b-col>
                    <b-col cols="12" md="6" xl="3">
                        <div class="stat-box">
                            <span class="field-label">실행 일정</span>
                            <div class="stat-value small-value">{{ scheduleLabel }}</div>
                            <small class="text-muted">{{ sourceLabel }}</small>
                        </div>
                    </b-col>
                </b-row>
            </b-card>

            <b-card class="mb-4" body-class="p-4">
                <div class="d-flex flex-column flex-md-row justify-content-between mb-3">
                    <div>
                        <h5 class="mb-1">자동 순환 제어</h5>
                        <p class="text-muted mb-0">사람이 개입해 자동 순환을 멈추거나 현재 버전을 고정합니다.</p>
                    </div>
                    <div v-if="manualRunVisible" class="mt-3 mt-md-0 text-md-right">
                        <b-button variant="outline-primary"
                                  size="sm"
                                  :disabled="manualRunDisabled"
                                  @click="runAutomation"
                        >
                            <b-spinner v-if="manualRunPending" small class="mr-1" />지금 한 회차 실행
                        </b-button>
                        <div v-if="manualRunBlockedReason" class="text-muted small mt-1">
                            {{ manualRunBlockedReason }}
                        </div>
                    </div>
                </div>

                <div v-for="item in switches" :key="item.name" class="switch-row">
                    <b-form-checkbox :key="switchKey(item)"
                                     :checked="item.value"
                                     :disabled="controlsDisabled"
                                     switch
                                     @change="onSwitchChange(item.name, $event)"
                    >
                        <span class="switch-label">{{ item.label }}</span>
                    </b-form-checkbox>
                    <div class="switch-description">{{ item.description }}</div>
                </div>
            </b-card>

            <b-card class="operating-model-card mb-4" body-class="p-4">
                <div class="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between">
                    <div>
                        <div class="eyebrow mb-2">기본 평가 단위</div>
                        <h5 class="mb-2">회사 답변 1건 → AI 평가 1건 → 담당자 피드백 1건</h5>
                        <p class="text-muted mb-0">
                            같은 문항에 여러 회사가 답해도 각 답변을 별도 사례로 다룹니다.
                            같은 답변을 여러 평가자가 반복해서 합의할 필요는 없습니다.
                        </p>
                    </div>
                    <b-badge variant="primary" class="single-feedback-badge mt-3 mt-lg-0">
                        단일 피드백 방식
                    </b-badge>
                </div>
            </b-card>

            <div class="section-heading mb-3">
                <div>
                    <h5 class="mb-1">피드백이 판단 기준이 되는 과정</h5>
                    <p class="text-muted mb-0">개별 사례를 복사하지 않고 여러 문항에 다시 쓸 수 있는 평가원칙으로 정리합니다.</p>
                </div>
            </div>

            <b-row class="flow-row mb-4">
                <b-col v-for="(step, index) in flowSteps" :key="step.title" cols="12" md="6" xl="3" class="mb-3">
                    <b-card class="flow-card h-100" body-class="p-3">
                        <div class="flow-step mb-2">{{ index + 1 }}</div>
                        <strong class="d-block mb-1">{{ step.title }}</strong>
                        <small class="text-muted">{{ step.description }}</small>
                    </b-card>
                </b-col>
            </b-row>

            <div class="d-flex flex-column flex-md-row align-items-md-end justify-content-between mb-3">
                <div>
                    <h5 class="mb-1">현재 평가원칙</h5>
                    <p class="text-muted mb-0">AI가 실제 판단할 때 우선 적용할 공통 기준입니다.</p>
                </div>
                <div class="text-muted small mt-2 mt-md-0">
                    {{ overview.rulebook.principleCount }}개 원칙
                </div>
            </div>

            <div v-if="overview.rulebook.principles.length === 0" class="empty-state text-center py-5">
                <strong class="d-block mb-2">아직 등록된 평가원칙이 없습니다.</strong>
                <span class="text-muted">AI 평가 피드백이 쌓이면 공통 원칙 후보가 만들어집니다.</span>
            </div>

            <b-card v-for="principle in overview.rulebook.principles"
                    v-else
                    :key="principle.ruleCode"
                    class="principle-card mb-3"
                    body-class="p-4"
            >
                <div class="d-flex flex-column flex-md-row justify-content-between mb-3">
                    <div>
                        <div class="d-flex align-items-center flex-wrap mb-2">
                            <b-badge variant="light" class="rule-code mr-2">{{ principle.ruleCode }}</b-badge>
                            <span class="topic-label">{{ principle.topic }}</span>
                        </div>
                        <h5 class="mb-0">{{ principle.title }}</h5>
                    </div>
                    <b-badge variant="success" class="align-self-start mt-2 mt-md-0">공통 원칙</b-badge>
                </div>
                <b-row>
                    <b-col cols="12" lg="6" class="mb-3">
                        <div class="principle-field">
                            <span class="field-label">언제 적용하나</span>
                            <p>{{ principle.appliesWhen }}</p>
                        </div>
                    </b-col>
                    <b-col cols="12" lg="6" class="mb-3">
                        <div class="principle-field emphasized">
                            <span class="field-label">어떻게 판단하나</span>
                            <p>{{ principle.ruleText }}</p>
                        </div>
                    </b-col>
                    <b-col cols="12" lg="6">
                        <div class="principle-field">
                            <span class="field-label">예외</span>
                            <p>{{ principle.exceptions }}</p>
                        </div>
                    </b-col>
                    <b-col cols="12" lg="6">
                        <div class="principle-field">
                            <span class="field-label">이유</span>
                            <p>{{ principle.rationale }}</p>
                        </div>
                    </b-col>
                </b-row>
            </b-card>
        </template>
    </section>
</template>

<script>
import {
    activationModeMeta,
    apiErrorMessage,
    automationStatusMeta,
    dailyCyclesStartedToday,
    formatKstDateTime,
    originMeta,
    sourceMeta,
} from './rulebook-presentation';

const JENKINS_SCHEDULE = 'daily 03:00 KST (Jenkins Rulebook Daily)';
const MANUAL_RUN_ADMIN_LEVEL = 4;

export default {
    name: 'RulebookOverviewTab',
    props: {
        overview: {type: Object, required: true},
        loading: {type: Boolean, default: false},
        error: {type: String, default: ''},
    },
    data() {
        return {
            switchPending: '',
            switchEpoch: 0,
            manualRunPending: false,
            manualRunForbidden: false,
            bootstrapPending: false,
        };
    },
    computed: {
        activationMode() {
            return activationModeMeta(this.overview.automation.activationMode);
        },
        automationStatus() {
            return automationStatusMeta(this.overview);
        },
        /** 상태 배지가 이미 '실행 중'을 말할 때는 같은 배지를 두 번 보이지 않는다. */
        runningBadgeVisible() {
            const rulebook = this.overview.rulebook;
            if (!this.overview.automation.running) return false;
            return !rulebook.automationActivationEnabled
                || rulebook.pinned
                || rulebook.candidateGenerationPaused
                || rulebook.activationPaused;
        },
        hasActiveVersion() {
            return this.overview.rulebook.activeVersionId !== null;
        },
        activeVersionLabel() {
            const active = this.overview.rulebook.activeVersion;
            if (active && active.versionNo !== null) return `v${active.versionNo}`;
            if (this.overview.rulebook.activeVersionId !== null) {
                return `#${this.overview.rulebook.activeVersionId}`;
            }
            return '—';
        },
        activeVersionOrigin() {
            const active = this.overview.rulebook.activeVersion;
            return active && active.origin ? originMeta(active.origin).label : '—';
        },
        activatedAtLabel() {
            const active = this.overview.rulebook.activeVersion;
            return active ? formatKstDateTime(active.activatedAt) : '—';
        },
        sourceLabel() {
            return sourceMeta(this.overview.rulebook.source).label;
        },
        scheduleLabel() {
            const schedule = this.overview.automation.schedule;
            if (!schedule || schedule === JENKINS_SCHEDULE) return '매일 03:00 (KST)';
            return schedule;
        },
        todayCycles() {
            return dailyCyclesStartedToday(this.overview.automation);
        },
        maxCycles() {
            return this.overview.automation.maxCyclesPerDay;
        },
        maxSources() {
            return this.overview.automation.maxSourcesPerCycle;
        },
        controlsDisabled() {
            return this.loading || Boolean(this.error) || this.switchPending !== '';
        },
        switches() {
            const rulebook = this.overview.rulebook;
            return [
                {
                    name: 'pinned',
                    label: '버전 고정',
                    description: '현재 활성 버전을 자동화가 바꾸지 못하게 합니다.',
                    value: rulebook.pinned,
                },
                {
                    name: 'candidate_generation_paused',
                    label: '후보 생성 중지',
                    description: '새 판단 기준 후보 생성을 멈춥니다.',
                    value: rulebook.candidateGenerationPaused,
                },
                {
                    name: 'activation_paused',
                    label: '활성화 중지',
                    description: '후보의 자동 활성화를 멈춥니다.',
                    value: rulebook.activationPaused,
                },
            ];
        },
        manualRunVisible() {
            if (this.manualRunForbidden) return false;
            const capability = this.overview.automation.canRunAutomation;
            // 백엔드가 권한을 알려주면 그것이 우선, 아니면 관리자 등급으로 선제 판단한다.
            if (capability !== null) return capability;
            return Number(this.$store?.state?.is_admin || 0) >= MANUAL_RUN_ADMIN_LEVEL;
        },
        manualRunBlockedReason() {
            const rulebook = this.overview.rulebook;
            if (this.overview.automation.running) return '자동 순환이 이미 실행 중입니다.';
            if (rulebook.pinned) return '버전 고정을 해제해야 실행할 수 있습니다.';
            if (rulebook.candidateGenerationPaused) return '후보 생성 중지를 해제해야 실행할 수 있습니다.';
            if (rulebook.activationPaused) return '활성화 중지 중에는 후보가 자동으로 켜지지 않습니다.';
            return '';
        },
        manualRunDisabled() {
            // 고정·중지 스위치가 하나라도 켜져 있으면 회차를 돌려도 의도한 결과가 나오지 않는다.
            return this.manualRunPending
                || this.controlsDisabled
                || Boolean(this.manualRunBlockedReason);
        },
        flowSteps() {
            const reviewOnly = this.overview.automation.activationMode === 'review_only';
            return [
                {title: 'AI 평가', description: '회사 답변과 증빙을 AI가 먼저 판단합니다.'},
                {title: '담당자 피드백', description: '담당자가 결과를 그대로 인정하거나 필요한 부분을 고칩니다.'},
                {title: '공통 원칙 추출', description: '비슷한 피드백의 문맥을 묶어 재사용 가능한 원칙으로 정리합니다.'},
                reviewOnly
                    ? {title: '검토 후 반영', description: '리뷰를 거친 후보가 자동으로 켜지고, 이상하면 이전 버전으로 되돌립니다.'}
                    : {title: '자동 검증', description: '숨겨둔 시험 사례에서 기존 기준보다 나쁘지 않은지 비교합니다.'},
            ];
        },
    },
    methods: {
        switchKey(item) {
            return `${item.name}-${this.switchEpoch}-${item.value}`;
        },
        notify(message, variant) {
            this.$bvToast.toast(message, {title: '판단 기준 운영', variant, solid: true});
        },
        async onSwitchChange(name, value) {
            if (this.switchPending) return;
            this.switchPending = name;
            try {
                await this.$axios.post('/eval-prompt/rulebook/switches', {[name]: value});
                this.notify('판단 기준 운영 설정을 변경했습니다.', 'success');
                this.$emit('refresh-overview');
            } catch (err) {
                // 실패하면 서버 상태가 정답이다. 토글은 다시 그려 원래 값으로 돌린다.
                this.switchEpoch += 1;
                this.notify(apiErrorMessage(err, '판단 기준 운영 설정을 변경하지 못했습니다.'), 'danger');
            } finally {
                this.switchPending = '';
            }
        },
        async runAutomation() {
            if (this.manualRunPending) return;
            this.manualRunPending = true;
            try {
                const response = await this.$axios.post('/eval-prompt/rulebook/automation/run');
                if (response?.data?.status === 'skipped') {
                    this.notify('오늘 실행 상한에 도달해 이번 요청은 실행하지 않았습니다.', 'warning');
                } else {
                    this.notify('자동 순환 한 회차를 실행 대기열에 넣었습니다.', 'success');
                }
                this.$emit('refresh-overview');
            } catch (err) {
                const status = err?.response?.status;
                if (status === 403) {
                    this.manualRunForbidden = true;
                    this.notify(apiErrorMessage(err, '자동 순환을 실행할 권한이 없습니다.'), 'danger');
                } else if (status === 409) {
                    this.notify(apiErrorMessage(err, '자동 순환이 이미 실행 중입니다.'), 'warning');
                    this.$emit('refresh-overview');
                } else {
                    this.notify(apiErrorMessage(err, '자동 순환을 시작하지 못했습니다.'), 'danger');
                }
            } finally {
                this.manualRunPending = false;
            }
        },
        async startBootstrap() {
            if (this.bootstrapPending) return;
            const confirmed = await this.$bvModal.msgBoxConfirm(
                '파일 시드를 v1 버전으로 발행하고 활성화합니다. 계속할까요?',
                {title: '기본 기준 발행', okTitle: '발행', cancelTitle: '취소', okVariant: 'primary'},
            );
            if (!confirmed) return;
            this.bootstrapPending = true;
            try {
                const response = await this.$axios.post('/eval-prompt/rulebook/bootstrap');
                if (response?.data?.is_active === true) {
                    this.notify('기본 기준을 v1 으로 발행했습니다.', 'success');
                } else {
                    this.notify('버전은 만들어졌지만 아직 활성 버전이 아닙니다. 버전 탭에서 확인해주세요.', 'warning');
                }
                this.$emit('refresh-overview');
            } catch (err) {
                this.notify(apiErrorMessage(err, '기본 기준을 발행하지 못했습니다.'), 'danger');
            } finally {
                this.bootstrapPending = false;
            }
        },
    },
};
</script>

<style scoped lang="scss">
.eyebrow,
.field-label {
    color: #52606d;
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
}

.operations-card {
    border-color: #dce3ea;
    box-shadow: 0 3px 12px rgba(31, 45, 61, 0.05);
}

.status-badge {
    font-size: 0.8rem;
    padding: 0.5rem 0.65rem;
}

.stat-box {
    background: #f8f9fa;
    border-radius: 6px;
    height: 100%;
    padding: 0.9rem 1rem;
}

.stat-value {
    color: #233142;
    font-size: 1.45rem;
    font-weight: 700;
    margin-top: 0.3rem;

    &.small-value {
        font-size: 1.05rem;
    }
}

.switch-row {
    border-top: 1px solid #edf1f5;
    padding: 0.85rem 0;

    &:first-of-type {
        border-top: 0;
        padding-top: 0;
    }
}

.switch-label {
    color: #29384a;
    font-weight: 600;
}

.switch-description {
    color: #687485;
    font-size: 0.85rem;
    padding-left: 2.4rem;
}

.operating-model-card {
    background: #f5f9ff;
    border: 1px solid #cfe0f7;
    border-left: 4px solid #2867b2;
}

.single-feedback-badge {
    font-size: 0.85rem;
    padding: 0.65rem 0.8rem;
}

.flow-card {
    border-color: #e1e6eb;
    box-shadow: 0 2px 8px rgba(31, 45, 61, 0.04);
}

.flow-step {
    align-items: center;
    background: #2867b2;
    border-radius: 50%;
    color: #fff;
    display: inline-flex;
    font-size: 0.78rem;
    font-weight: 700;
    height: 1.75rem;
    justify-content: center;
    width: 1.75rem;
}

.principle-card {
    border-color: #dce3ea;
    box-shadow: 0 3px 12px rgba(31, 45, 61, 0.05);
}

.rule-code {
    border: 1px solid #ccd6e0;
    color: #364152;
    font-family: monospace;
}

.topic-label {
    color: #6b7785;
    font-size: 0.82rem;
}

.principle-field {
    background: #f8f9fa;
    border-radius: 6px;
    height: 100%;
    padding: 0.9rem 1rem;

    &.emphasized {
        background: #eef6ff;
    }

    p {
        color: #354052;
        font-size: 0.9rem;
        line-height: 1.6;
        margin: 0.35rem 0 0;
    }
}

.empty-state {
    background: #fafbfc;
    border: 1px dashed #cbd3dc;
    border-radius: 8px;
}
</style>
